import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import auth from '../../utils/auth';

const SessionExpirationChecker = () => {
  const [showModal, setShowModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const history = useHistory();
  
  // Function to extract expiration from JWT token
  const getTokenExpiration = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    
    try {
      // Extract payload from JWT token
      const payload = token.split('.')[1];
      // Decode base64 payload
      const decodedPayload = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      
      if (decodedPayload.exp) {
        return decodedPayload.exp;
      }
    } catch (error) {
      console.error('Error parsing JWT token:', error);
    }
    
    return null;
  }, []);
  
  // Check token expiration time
  const checkTokenExpiration = useCallback(() => {
    const exp = getTokenExpiration();
    if (!exp) return;
    
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    const timeLeft = exp - currentTime; // Seconds until expiration
    
    // Update time remaining
    setTimeRemaining(timeLeft);
    
    // If within 60 seconds of expiration, show the modal
    if (timeLeft <= 60 && timeLeft > 0) {
      setShowModal(true);
    } else if (timeLeft <= 0) {
      // Token already expired
      handleLogout();
    }
  }, [getTokenExpiration]);
  
  // Handle logout and redirect
  const handleLogout = async () => {
    setIsProcessing(true);
    try {
      await auth.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // Redirect to login page
      history.push('/');
    }
  };
  
  useEffect(() => {
    // Check immediately when component mounts
    checkTokenExpiration();
    
    // Set up interval to check periodically (every 10 seconds)
    const intervalId = setInterval(checkTokenExpiration, 10000);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [checkTokenExpiration]);
  
  // If modal shouldn't be shown, render nothing
  if (!showModal) {
    return null;
  }
  
  // Modal overlay styles for proper positioning and z-index
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    backdropFilter: 'blur(4px)',
    transition: 'all 0.3s ease'
  };
  
  // Modal content styles
  const modalContentStyle = {
    position: 'relative',
    margin: '0 auto',
    zIndex: 2060,
    maxWidth: '450px',
    width: '90%',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    animation: 'modalFadeIn 0.3s ease-out',
    border: '1px solid rgba(0,0,0,0.1)'
  };

  const modalHeaderStyle = {
    padding: '20px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    alignItems: 'center'
  };

  const modalTitleStyle = {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#343a40'
  };

  const modalBodyStyle = {
    padding: '24px',
    fontSize: '16px',
    lineHeight: 1.5,
    color: '#495057'
  };

  const modalFooterStyle = {
    padding: '16px 24px',
    borderTop: '1px solid #e9ecef',
    display: 'flex',
    justifyContent: 'flex-end',
    backgroundColor: '#f8f9fa'
  };

  const buttonStyle = {
    padding: '10px 20px',
    backgroundColor: '#02a5e2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'background-color 0.2s ease'
  };

  const warningTextStyle = {
    color: timeRemaining <= 30 ? '#dc3545' : '#fd7e14',
    fontWeight: 600
  };
  
  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={modalHeaderStyle}>
          <h5 style={modalTitleStyle}>
            <span role="img" aria-label="warning" style={{marginRight: '10px'}}>⚠️</span>
            Session Expired
          </h5>
        </div>
        <div style={modalBodyStyle}>
          <p>Your session has expired</p>
          <p>Please log in again to continue.</p>
        </div>
        <div style={modalFooterStyle}>
          <button
            type="button"
            style={buttonStyle}
            onMouseOver={(e) => !isProcessing && (e.target.style.backgroundColor = '#0294cb')}
            onMouseOut={(e) => !isProcessing && (e.target.style.backgroundColor = '#02a5e2')}
            onClick={handleLogout}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Login Again'}
          </button>
        </div>
      </div>
    </div>
  );
};


export default SessionExpirationChecker;